<template>
  <b-modal
    v-model="modal"
    size="lg"
    hide-footer
    body-class="p-0"
    :no-enforce-focus="true"
    :no-close-on-backdrop="true"
  >
    <template #modal-title>
      Gerenciar Grupos <span v-if="totalRows > 0" class="badge badge-soft-primary font-size-12 ml-1">{{ totalRows }}</span>
    </template>
    <loading
      :active.sync="loading"
      color="#556ee6"
      loader="dots"
      :can-cancel="false"
      :is-full-page="false"
    />

    <!-- ========== LIST VIEW ========== -->
    <template v-if="view === 'list'">
      <!-- Toolbar -->
      <div class="d-flex align-items-center px-3 pt-3 pb-2">
        <div class="flex-grow-1 mr-2">
          <div class="position-relative">
            <i class="bx bx-search position-absolute" style="left: 10px; top: 50%; transform: translateY(-50%); color: #74788d; font-size: 16px;"></i>
            <input
              v-model="search"
              type="text"
              class="form-control"
              placeholder="Buscar grupo..."
              style="padding-left: 34px;"
              @input="onSearch"
            />
          </div>
        </div>
        <button v-if="canManage" type="button" class="btn btn-outline-primary mr-2" @click="view = 'import'" style="white-space: nowrap;">
          <i class="bx bx-upload mr-1"></i> Importar Planilha
        </button>
        <button v-if="canManage" type="button" class="btn btn-primary" @click="newGroup()" style="white-space: nowrap;">
          <i class="bx bx-plus mr-1"></i> Novo Grupo
        </button>
      </div>

      <!-- Groups Cards -->
      <div class="px-3 pb-2" v-if="groupsList && groupsList.length > 0">
        <div
          v-for="g in groupsList"
          :key="g.id"
          class="card border shadow-none mb-2"
        >
          <div class="card-body p-3">
            <div class="d-flex align-items-center justify-content-between">
              <div class="flex-grow-1" style="min-width: 0;">
                <h6 class="mb-1 font-size-14 text-truncate">{{ g.name }}</h6>
                <div class="d-flex align-items-center flex-wrap" style="gap: 6px;">
                  <span class="text-muted font-size-12">
                    Limite: <strong>{{ g.limit_register > 0 ? g.limit_register : '∞' }}</strong>
                  </span>
                  <span class="text-muted font-size-12">·</span>
                  <span class="text-muted font-size-12">
                    Itens: <strong>{{ g.items_count || 0 }}</strong>
                  </span>
                  <span class="text-muted font-size-12">·</span>
                  <span
                    class="badge font-size-12"
                    :class="isGroupFull(g) ? 'badge-soft-danger' : 'badge-soft-success'"
                  >
                    <i class="bx bx-check mr-1" style="font-size: 11px;"></i>{{ g.used_count || 0 }}
                  </span>
                </div>
              </div>
              <div v-if="canManage" class="d-flex align-items-center ml-2" style="gap: 4px;">
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm"
                  @click="editGroup(g)"
                  title="Editar"
                >
                  <i class="bx bx-edit"></i>
                </button>
                <button
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  @click="deleteGroup(g)"
                  title="Excluir"
                >
                  <i class="bx bx-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!loading" class="text-center py-5 px-3">
        <div class="group-icon-wrapper d-inline-flex mb-3">
          <i class="bx bx-collection"></i>
        </div>
        <p class="text-muted mb-0 font-size-14">Nenhum grupo cadastrado</p>
        <p class="text-muted font-size-12" v-if="search">Tente buscar com outros termos</p>
      </div>

      <!-- Pagination -->
      <div class="d-flex justify-content-center px-3 pb-3" v-if="totalRows > perPage">
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          size="sm"
        />
      </div>

      <!-- Footer -->
      <hr class="mb-3 mt-1 mx-3" />
      <div class="d-flex justify-content-end px-3 pb-3">
        <b-button variant="light" @click="close()">Fechar</b-button>
      </div>
    </template>

    <!-- ========== FORM VIEW ========== -->
    <template v-if="view === 'form'">
      <!-- Header bar -->
      <div class="d-flex align-items-center px-3 pt-3 pb-2 border-bottom">
        <button type="button" class="btn btn-link p-0 text-muted mr-2" @click="backToList()">
          <i class="bx bx-arrow-back font-size-18"></i>
        </button>
        <h6 class="mb-0 font-size-15">{{ group.id ? 'Editar Grupo' : 'Novo Grupo' }}</h6>
      </div>

      <!-- Form Content -->
      <div class="px-3 py-4">
        <!-- Decorative Icon -->
        <div class="text-center mb-4">
          <div class="group-icon-wrapper d-inline-flex">
            <i class="bx bx-collection"></i>
          </div>
        </div>

        <form @submit.prevent="saveGroupData()">
          <div class="form-group">
            <label class="font-size-13 font-weight-medium">Nome do Grupo <span class="text-danger">*</span></label>
            <input
              v-model="group.name"
              type="text"
              class="form-control"
              placeholder="Ex: Região Norte, Setor A, Categoria VIP"
              ref="groupNameInput"
            />
            <small class="text-muted font-size-12">Ex: Região Norte, Setor A, Categoria VIP</small>
          </div>

          <div class="form-group">
            <label class="font-size-13 font-weight-medium">Limite de cadastros</label>
            <input
              v-model.number="group.limit_register"
              type="number"
              class="form-control"
              min="0"
            />
            <small class="text-muted font-size-12">Deixe 0 para ilimitado</small>
          </div>

          <div class="form-group mb-0">
            <label class="font-size-13 font-weight-medium">Formulário específico</label>
            <select v-model="group.form_id" class="form-control">
              <option :value="null">— Usar formulário padrão do controle —</option>
              <option v-for="f in forms" :key="f.id" :value="f.id">{{ f.name }}</option>
            </select>
            <small class="text-muted font-size-12">Se definido, participantes deste grupo usarão este formulário</small>
          </div>
        </form>
      </div>

      <!-- Footer -->
      <hr class="mb-3 mt-1 mx-3" />
      <div class="d-flex justify-content-between px-3 pb-3">
        <b-button variant="light" @click="backToList()">Cancelar</b-button>
        <b-button variant="primary" @click="saveGroupData()" :disabled="saving">
          <i class="bx bx-check mr-1" v-if="!saving"></i>
          <i class="bx bx-loader-alt bx-spin mr-1" v-else></i>
          Salvar
        </b-button>
      </div>
    </template>

    <!-- ========== IMPORT VIEW ========== -->
    <template v-if="view === 'import'">
      <!-- Step 1: Upload -->
      <div v-if="importStep === 1" class="px-3 pt-3 pb-0">
        <!-- Header -->
        <div class="d-flex align-items-center mb-3">
          <button type="button" class="btn btn-link p-0 text-muted mr-2" @click="backToList()">
            <i class="bx bx-arrow-back font-size-18"></i>
          </button>
          <h6 class="mb-0 font-size-15">Importar Grupos</h6>
        </div>

        <div class="text-center mb-4">
          <div class="group-icon-wrapper d-inline-flex mb-3">
            <i class="bx bx-spreadsheet"></i>
          </div>
          <h5 class="mb-1">Importação em massa de grupos</h5>
          <p class="text-muted mb-0">Adicione centenas de grupos de uma só vez usando uma planilha Excel ou CSV.</p>
        </div>

        <!-- Columns info -->
        <div class="card border shadow-none mb-3">
          <div class="card-body p-3">
            <h6 class="mb-3"><i class="bx bx-columns text-primary mr-1"></i> Colunas da planilha</h6>
            <div class="table-responsive">
              <table class="table table-sm table-bordered mb-0 font-size-13">
                <thead class="thead-light">
                  <tr>
                    <th style="width: 30%">Coluna</th>
                    <th style="width: 15%">Tipo</th>
                    <th>Descrição</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>nome</code> <span class="badge badge-soft-danger font-size-10 ml-1">obrigatório</span></td>
                    <td><span class="text-muted">texto</span></td>
                    <td>Nome do grupo. Se já existir um grupo com o mesmo nome, ele será <strong>atualizado</strong>.</td>
                  </tr>
                  <tr>
                    <td><code>limite</code></td>
                    <td><span class="text-muted">número</span></td>
                    <td>Limite de cadastros permitidos. Deixe vazio ou 0 para ilimitado.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Download template -->
        <div class="text-center mb-3">
          <button type="button" class="btn btn-outline-success" @click="downloadTemplate()">
            <i class="bx bx-download mr-1"></i> Baixar Template (.xlsx)
          </button>
        </div>

        <!-- Drop zone -->
        <div
          class="drop-zone mb-3"
          :class="{ 'drop-zone-active': isDragging, 'drop-zone-has-file': importFile }"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleDrop"
          @click="$refs.fileInput.click()"
        >
          <input
            ref="fileInput"
            type="file"
            accept=".xlsx,.xls,.csv"
            class="d-none"
            @change="handleFileSelect"
          />
          <div v-if="!importFile" class="text-center">
            <i class="bx bx-cloud-upload drop-zone-icon text-muted"></i>
            <p class="mb-1">Arraste o arquivo aqui ou <strong class="text-primary">clique para selecionar</strong></p>
            <small class="text-muted">Formatos aceitos: .xlsx, .xls, .csv</small>
          </div>
          <div v-else class="text-center">
            <i class="bx bx-file drop-zone-icon text-success"></i>
            <p class="mb-1 font-weight-medium">{{ importFile.name }}</p>
            <small class="text-muted">{{ formatFileSize(importFile.size) }}</small>
            <br />
            <a href="#" @click.stop.prevent="importFile = null" class="text-danger font-size-12 mt-1 d-inline-block">
              <i class="bx bx-x"></i> Remover arquivo
            </a>
          </div>
        </div>
      </div>

      <!-- Step 1 Footer -->
      <template v-if="importStep === 1">
        <hr class="mb-3 mt-1 mx-3" />
        <div class="d-flex justify-content-between px-3 pb-3">
          <b-button variant="light" @click="backToList()">
            <i class="bx bx-left-arrow-alt mr-1"></i> Voltar
          </b-button>
          <b-button variant="primary" :disabled="!importFile || loading" @click="applyImport()">
            <i class="bx bx-upload mr-1"></i> Importar Grupos
          </b-button>
        </div>
      </template>

      <!-- Step 2: Processing -->
      <div v-if="importStep === 2" class="text-center py-5 px-3">
        <div class="group-icon-wrapper d-inline-flex mb-3">
          <i class="bx bx-loader-alt bx-spin"></i>
        </div>
        <h5 class="mb-2">Processando planilha...</h5>
        <p class="text-muted mb-3">Aguarde enquanto os grupos são importados.</p>
        <div class="progress mx-auto" style="max-width: 400px; height: 8px;">
          <div
            class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
            role="progressbar"
            :style="{ width: progressPercent + '%' }"
          ></div>
        </div>
        <p class="text-muted font-size-13 mt-2 mb-0">{{ progressPercent }}%</p>
      </div>

      <!-- Step 3: Results -->
      <div v-if="importStep === 3" class="px-3 pt-3 pb-0">
        <div class="text-center">
          <div class="group-icon-wrapper d-inline-flex mb-3" :class="{ 'group-icon-success': importResult.errors === 0, 'group-icon-warning': importResult.errors > 0 }">
            <i :class="importResult.errors > 0 ? 'bx bx-error' : 'bx bx-check-circle'"></i>
          </div>
          <h5 class="mb-2">Importação concluída!</h5>
        </div>

        <div class="row justify-content-center mt-3 mb-3">
          <div class="col-auto">
            <div class="result-card result-card-success">
              <span class="result-number">{{ importResult.imported }}</span>
              <span class="result-label">importados</span>
            </div>
          </div>
          <div class="col-auto" v-if="importResult.errors > 0">
            <div class="result-card result-card-danger">
              <span class="result-number">{{ importResult.errors }}</span>
              <span class="result-label">erros</span>
            </div>
          </div>
        </div>

        <p class="text-muted text-center" v-if="importResult.errors === 0">Todos os grupos foram processados com sucesso.</p>

        <!-- Error list -->
        <div v-if="errorLogEntries.length > 0" class="mt-3">
          <h6 class="mb-2"><i class="bx bx-x-circle text-danger mr-1"></i> Grupos com erro</h6>
          <div class="border rounded" style="max-height: 250px; overflow-y: auto; background: #f8f9fa;">
            <div
              v-for="(entry, i) in errorLogEntries"
              :key="i"
              class="d-flex align-items-start py-2 px-3"
              :class="{ 'border-bottom': i < errorLogEntries.length - 1 }"
            >
              <i class="bx bx-x-circle text-danger mr-2 mt-1 font-size-14"></i>
              <div class="font-size-13">
                <strong v-if="entry.name">{{ entry.name }}</strong>
                <span v-if="entry.message" class="d-block text-danger font-size-12 mt-1">{{ entry.message }}</span>
              </div>
            </div>
          </div>
        </div>

        <hr class="mb-3 mt-3" />

        <div class="text-center pb-3">
          <b-button variant="primary" @click="finishImport()">Fechar</b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import Swal from 'sweetalert2'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { mapActions, mapState } from 'vuex'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { importGroupsFile, downloadGroupsTemplate, listForms } from './services'

export default {
  components: {
    Loading
  },
  props: {
    controlId: {
      type: [Number, String],
      required: true
    },
    canManage: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      loading: false,
      saving: false,
      modal: false,
      view: 'list',
      search: '',
      currentPage: 1,
      perPage: 20,
      searchTimeout: null,
      group: {
        name: '',
        limit_register: 0,
        form_id: null
      },
      forms: [],
      // Import
      importStep: 1,
      importFile: null,
      isDragging: false,
      progressPercent: 0,
      progressTimer: null,
      importResult: {
        imported: 0,
        errors: 0,
        log: []
      }
    }
  },
  computed: {
    ...mapState('registrationControl', {
      groupsList: 'groups',
      groupsPagination: 'groupsPagination'
    }),
    totalRows () {
      return this.groupsPagination && this.groupsPagination.total ? this.groupsPagination.total : 0
    },
    errorLogEntries () {
      return this.importResult.log.filter(e => e.status === 'error')
    }
  },
  methods: {
    ...mapActions('registrationControl', {
      getGroupsAction: 'getGroups',
      saveGroupAction: 'saveGroup',
      removeGroupAction: 'removeGroup'
    }),
    open () {
      this.modal = true
      this.view = 'list'
      this.search = ''
      this.currentPage = 1
      this.resetImport()
      this.loadGroups()
      this.loadForms()
    },
    async loadForms () {
      try {
        const res = await listForms({ limit: 200, page: 1 })
        this.forms = res.data.data || []
      } catch (e) {
        this.forms = []
      }
    },
    loadGroups () {
      this.loading = true
      const params = {
        limit: this.perPage,
        page: this.currentPage
      }
      if (this.search) {
        params.search = this.search
      }
      this.getGroupsAction({ controlId: this.controlId, params }).then(() => {
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    onSearch () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadGroups()
      }, 400)
    },
    isGroupFull (g) {
      return g.limit_register > 0 && (g.used_count || 0) >= g.limit_register
    },
    newGroup () {
      this.group = { name: '', limit_register: 0, form_id: null }
      this.view = 'form'
      this.$nextTick(() => {
        if (this.$refs.groupNameInput) {
          this.$refs.groupNameInput.focus()
        }
      })
    },
    editGroup (g) {
      this.group = { ...g }
      this.view = 'form'
      this.$nextTick(() => {
        if (this.$refs.groupNameInput) {
          this.$refs.groupNameInput.focus()
        }
      })
    },
    backToList () {
      this.view = 'list'
      this.group = { name: '', limit_register: 0, form_id: null }
      this.resetImport()
    },
    async saveGroupData () {
      if (!this.group.name) {
        errorMessage('Alerta: O nome do grupo é obrigatório.')
        return
      }
      this.saving = true
      try {
        await this.saveGroupAction({ controlId: this.controlId, data: this.group })
        successMessage('Sucesso: Grupo salvo com sucesso.', 2500)
        this.view = 'list'
        this.group = { name: '', limit_register: 0, form_id: null }
        this.loadGroups()
      } catch (err) {
        errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
      } finally {
        this.saving = false
      }
    },
    deleteGroup (g) {
      Swal.fire({
        title: 'Você tem certeza ?',
        html: `Será excluído o grupo: <b>${g.name}</b>`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'CANCELAR',
        confirmButtonText: 'SIM, DESEJO EXCLUIR!'
      }).then(result => {
        if (result.value) {
          this.loading = true
          this.removeGroupAction({ controlId: this.controlId, id: g.id }).then(() => {
            successMessage('Sucesso: Grupo excluído com sucesso.', 2500)
            this.loadGroups()
          }).catch((err) => {
            this.loading = false
            errorMessage('Alerta: ' + (err.response ? err.response.data.message : err.message))
          })
        }
      })
    },
    // Import methods
    resetImport () {
      this.importStep = 1
      this.importFile = null
      this.isDragging = false
      this.progressPercent = 0
      this.importResult = { imported: 0, errors: 0, log: [] }
      this.stopProgress()
    },
    handleFileSelect (e) {
      const files = e.target.files
      if (files && files.length > 0) {
        this.importFile = files[0]
      }
    },
    handleDrop (e) {
      this.isDragging = false
      const files = e.dataTransfer.files
      if (files && files.length > 0) {
        const file = files[0]
        const ext = file.name.split('.').pop().toLowerCase()
        if (['xlsx', 'xls', 'csv'].includes(ext)) {
          this.importFile = file
        } else {
          errorMessage('Alerta: Formato não suportado. Use .xlsx, .xls ou .csv')
        }
      }
    },
    formatFileSize (bytes) {
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / 1048576).toFixed(1) + ' MB'
    },
    async downloadTemplate () {
      try {
        const res = await downloadGroupsTemplate(this.controlId)
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'template_importacao_grupos.xlsx')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        errorMessage('Alerta: Falha ao baixar template.')
      }
    },
    async applyImport () {
      if (!this.importFile) return
      this.importStep = 2
      this.progressPercent = 0
      this.startProgress()
      try {
        const formData = new FormData()
        formData.append('file', this.importFile)
        const res = await importGroupsFile(this.controlId, formData)
        this.importResult = {
          imported: res.data.imported || 0,
          errors: res.data.errors || 0,
          log: res.data.log || []
        }
        this.stopProgress()
        this.progressPercent = 100
        await new Promise(resolve => setTimeout(resolve, 300))
        this.importStep = 3
      } catch (err) {
        this.stopProgress()
        this.importStep = 1
        const msg = err.response && err.response.data ? err.response.data.message : err.message
        errorMessage('Alerta: ' + msg)
      }
    },
    startProgress () {
      this.progressTimer = setInterval(() => {
        if (this.progressPercent < 90) {
          const remaining = 90 - this.progressPercent
          const increment = Math.max(0.5, remaining * 0.08)
          this.progressPercent = Math.min(90, Math.round((this.progressPercent + increment) * 10) / 10)
        }
      }, 200)
    },
    stopProgress () {
      if (this.progressTimer) {
        clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    finishImport () {
      this.resetImport()
      this.view = 'list'
      this.loadGroups()
    },
    close () {
      this.modal = false
      this.view = 'list'
      this.search = ''
      this.currentPage = 1
      this.group = { name: '', limit_register: 0, form_id: null }
      this.resetImport()
    }
  },
  watch: {
    modal () {
      if (!this.modal) {
        this.close()
      }
    },
    currentPage () {
      this.loadGroups()
    }
  },
  beforeDestroy () {
    this.stopProgress()
  }
}
</script>

<style scoped>
.group-icon-wrapper {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background-color: rgba(85, 110, 230, 0.1);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.group-icon-wrapper i {
  font-size: 28px;
  color: #556ee6;
}
.group-icon-success {
  background-color: rgba(52, 195, 143, 0.1) !important;
}
.group-icon-success i {
  color: #34c38f !important;
}
.group-icon-warning {
  background-color: rgba(241, 180, 76, 0.1) !important;
}
.group-icon-warning i {
  color: #f1b44c !important;
}

/* Drop zone */
.drop-zone {
  border: 2px dashed #ced4da;
  border-radius: 8px;
  padding: 30px 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fafafa;
}
.drop-zone:hover {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.03);
}
.drop-zone-active {
  border-color: #556ee6;
  background: rgba(85, 110, 230, 0.06);
}
.drop-zone-has-file {
  border-color: #34c38f;
  border-style: solid;
  background: rgba(52, 195, 143, 0.03);
}
.drop-zone-icon {
  font-size: 36px;
  display: block;
  margin-bottom: 8px;
}

/* Result cards */
.result-card {
  text-align: center;
  padding: 16px 28px;
  border-radius: 8px;
  min-width: 120px;
}
.result-card-success {
  background: rgba(52, 195, 143, 0.1);
}
.result-card-danger {
  background: rgba(244, 106, 106, 0.1);
}
.result-number {
  display: block;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}
.result-card-success .result-number {
  color: #34c38f;
}
.result-card-danger .result-number {
  color: #f46a6a;
}
.result-label {
  display: block;
  font-size: 12px;
  color: #74788d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

/* Table */
.table-sm td, .table-sm th {
  padding: 0.4rem 0.6rem;
}
code {
  font-size: 12px;
}

.vld-overlay.is-full-page {
  z-index: 1051 !important;
}
</style>
